import { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  EmulatorType,
  GetInvitationResponse,
  LicenseAssignment,
  Permission,
  Subscription,
  UserMembershipView,
  UserRole,
} from '@localstack/types';

import { SUB_ACTIVE_STATUSES } from '@localstack/constants';

import { Box } from '@mui/material';

import { useSnackbar } from '@localstack/services';

import { EditMemberPermissionsModal } from '~/components/MembersAndLicenses/EditMemberPermissionsModal';

import { MembersToolbar } from './MembersToolbar';
import { Filter, LicenseAssignmentsByUser, ScimStatus } from './types';
import {
  applyInvitationFilter,
  applyMembershipFilter,
  computeSubscriptionFreeSeatAvailability,
  indexAssignmentsByUserId,
  indexInvitationsById,
  indexMembersById,
  setUserAssignments,
} from './utils';
import { MembersList } from './MembersList';

type MembersAndLicenses = {
  subscriptions: Subscription[];
  areSubscriptionsLoading: boolean;
  licenseAssignments: LicenseAssignment[];
  areLicenseAssignmentsLoading: boolean;
  members: UserMembershipView[];
  invitations: GetInvitationResponse[];
  isMembersLoading: boolean;
  isInvitationsLoading: boolean;
  canUpdateMembers: boolean;
  canDeleteMembers: boolean;
  deleteMember: (id: string) => Promise<Optional<UserMembershipView>>;
  deleteInvitation: (id: string) => Promise<void>;
  updateMembershipRole: (userId: string, role: UserRole) => Promise<Optional<UserMembershipView>>;
  updateMembershipPermissions: (userId: string, permissions: string[]) => Promise<Optional<UserMembershipView>>;
  resendInvitation?: (id: string) => Promise<GetInvitationResponse>;
  setLicenseAssignmentsForUser: (userId: string, subscriptionIds: string[]) => Promise<LicenseAssignment[]>;
  onUserClick?: (userId: string) => void;
  spaceSearchBar?: boolean;
  scimIdpName?: string | null;
  children: ReactElement;
};
export const MembersAndLicenses = ({
  subscriptions,
  areSubscriptionsLoading,
  licenseAssignments,
  areLicenseAssignmentsLoading,
  members,
  invitations,
  isMembersLoading,
  isInvitationsLoading,
  canUpdateMembers,
  canDeleteMembers,
  deleteMember,
  deleteInvitation,
  updateMembershipRole,
  updateMembershipPermissions,
  resendInvitation,
  setLicenseAssignmentsForUser,
  onUserClick,
  spaceSearchBar,
  scimIdpName,
  children,
}: MembersAndLicenses): ReactElement => {
  const { showSnackbar } = useSnackbar();

  const scimInfo: ScimStatus = { active: !!scimIdpName, idpName: scimIdpName ?? null };

  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [searchQueryFilter, setSearchQueryFilter] = useState<Filter>(Filter.ALL);
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('');
  const [licenseAssignmentsState, setLicenseAssignmentsState] = useState<LicenseAssignmentsByUser>({});
  const [membersState, setMembersState] = useState<Record<string, UserMembershipView>>({});
  const [invitationsState, setInvitationsState] = useState<Record<string, GetInvitationResponse>>({});
  const [editMemberPermissionsId, setEditMemberPermissionsId] = useState<string | null>(null);

  const activeSubscriptions = useMemo(
    () =>
      Object.fromEntries(
        subscriptions.filter((sub) => SUB_ACTIVE_STATUSES.includes(sub.status)).map((sub) => [sub.id, sub]),
      ),
    [subscriptions],
  );

  const subscriptionsById = useMemo(
    () => Object.fromEntries(subscriptions.map((sub) => [sub.id, sub])),
    [subscriptions],
  );

  const subscriptionsSeatsAvailability = useMemo(
    () => computeSubscriptionFreeSeatAvailability(subscriptionsById, licenseAssignmentsState),
    [subscriptionsById, licenseAssignmentsState],
  );

  const subscriptionsByEmulator = useMemo(
    () =>
      Object.values(activeSubscriptions).reduce(
        (memo, sub) => ({ ...memo, [sub.plan.emulator]: { ...memo[sub.plan.emulator], [sub.id]: sub } }),
        {} as Record<EmulatorType, Record<string, Subscription>>,
      ),
    [activeSubscriptions],
  );

  const filteredMembers = Object.values(membersState).filter(
    applyMembershipFilter(searchQueryFilter, searchQuery, licenseAssignmentsState, subscriptionFilter),
  );

  const adminCount = Object.values(membersState).filter(
    (member) => member.membership_details.role === UserRole.ADMIN,
  ).length;

  const filteredInvitations = Object.values(invitationsState).filter(
    applyInvitationFilter(searchQueryFilter, searchQuery),
  );

  useEffect(() => {
    setLicenseAssignmentsState(indexAssignmentsByUserId(licenseAssignments));
  }, [licenseAssignments]);

  useEffect(() => {
    setMembersState(indexMembersById(members));
  }, [members]);

  useEffect(() => {
    setInvitationsState(indexInvitationsById(invitations));
  }, [invitations]);

  const setUserLicenses = async (userId: string, subscriptionIds: string[]) => {
    const updatedAssignments = await setLicenseAssignmentsForUser(userId, subscriptionIds);
    if (!updatedAssignments) return;
    setLicenseAssignmentsState((prev) => setUserAssignments(prev, userId, updatedAssignments));
  };

  const updateMemberRole = async (userId: string, role: UserRole) => {
    const updatedMember = await updateMembershipRole(userId, role);
    if (!updatedMember) return;
    setMembersState((prev) => ({ ...prev, [userId]: updatedMember }));
  };

  const removeMemberFromWorkspace = async (userId: string) => {
    const removedMember = await deleteMember(userId);
    if (!removedMember) return;
    setMembersState((prev) => {
      const { [userId]: _, ...rest } = prev;
      return rest;
    });
  };

  const toggleMemberPermission = async (userId: string, permission: Permission) => {
    const editedMember = membersState[userId];
    if (!editedMember) return;

    let updatedPermissions = [...editedMember.membership_details.permissions];
    if (editedMember.membership_details.permissions.includes(permission)) {
      updatedPermissions = updatedPermissions.filter((p) => p !== permission);
    } else {
      updatedPermissions.push(permission);
    }

    const updatedMember = await updateMembershipPermissions(userId, updatedPermissions);
    if (!updatedMember) return;
    setMembersState((prev) => ({ ...prev, [userId]: updatedMember }));
  };

  const handleResendInvitation = resendInvitation
    ? async (invitationId: string) => {
        const updatedInvitation = await resendInvitation(invitationId);
        showSnackbar({ message: 'Invation has been resent', severity: 'success' });
        setInvitationsState((prev) => ({ [updatedInvitation.id]: updatedInvitation, ...prev }));
      }
    : undefined;

  const handleRevokeInvitation = async (invitationId: string) => {
    await deleteInvitation(invitationId);
    showSnackbar({ message: 'Invitation has been revoked', severity: 'success' });
    setInvitationsState((prev) => {
      const { [invitationId]: _, ...rest } = prev;
      return rest;
    });
  };

  return (
    <Box>
      {editMemberPermissionsId && membersState[editMemberPermissionsId] && (
        <EditMemberPermissionsModal
          member={membersState[editMemberPermissionsId]}
          onClose={() => setEditMemberPermissionsId(null)}
          onTogglePermission={toggleMemberPermission}
        />
      )}
      <MembersToolbar
        subscriptionsByEmulator={subscriptionsByEmulator}
        subscriptionsSeatsAvailability={subscriptionsSeatsAvailability}
        onSearchChange={setSearchQuery}
        searchQueryFilter={searchQueryFilter}
        onFilterChange={setSearchQueryFilter}
        subscriptionFilter={subscriptionFilter}
        onSubscriptionFilterChange={setSubscriptionFilter}
        activeSubscriptions={Object.values(activeSubscriptions)}
        isLoading={areSubscriptionsLoading || areLicenseAssignmentsLoading}
        leftSpace={spaceSearchBar ?? false}
      >
        {children}
      </MembersToolbar>
      <MembersList
        totalMembersCount={Object.keys(membersState).length}
        totalInvitationCount={Object.keys(invitationsState).length}
        isMembersLoading={isMembersLoading}
        isInvitationsLoading={isInvitationsLoading}
        members={filteredMembers}
        adminCount={adminCount}
        resetScrollKey={`${searchQuery}|${subscriptionFilter}|${searchQueryFilter}`}
        subscriptions={subscriptions}
        assignmentsByUser={licenseAssignmentsState}
        seatsAvailability={subscriptionsSeatsAvailability}
        invitations={filteredInvitations}
        canUpdateMembers={canUpdateMembers}
        canDeleteMembers={canDeleteMembers}
        onResendInvitation={handleResendInvitation}
        onRevokeInvitation={handleRevokeInvitation}
        onUpdateMemberRole={updateMemberRole}
        onRemoveMember={removeMemberFromWorkspace}
        setUserLicenses={setUserLicenses}
        setEditMemberPermissionsId={(memberId: string) => setEditMemberPermissionsId(memberId)}
        onUserClick={onUserClick}
        scimInfo={scimInfo}
      />
    </Box>
  );
};
